import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, Minus, Trash2, ShoppingBag, ScanLine, Send } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { SriStatusBanner } from "@/pages/Customers";
import { toast } from "sonner";

const PAYMENT_OPTIONS = [
  { v: "efectivo", l: "Efectivo" },
  { v: "tarjeta", l: "Tarjeta" },
  { v: "transferencia", l: "Transferencia" },
  { v: "credito", l: "Crédito" },
];

export default function Pos() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [customer_id, setCustomerId] = useState("");
  const [payment_method, setPayment] = useState("efectivo");
  const [submitting, setSubmitting] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const selectedCustomer = customers.find((c) => c.id === customer_id) || null;
  const searchRef = useRef(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active)));
    api.get("/customers").then((r) => {
      setCustomers(r.data);
      // Auto-select Consumidor Final if exists
      const cf = r.data.find((c) => c.identification_type === "consumidor_final");
      if (cf) setCustomerId(cf.id);
    });
    searchRef.current?.focus();
  }, []);

  // Keyboard shortcut: F2 focuses search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return products.slice(0, 24);
    return products
      .filter((p) =>
        p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
      )
      .slice(0, 24);
  }, [products, search]);

  const totals = useMemo(() => {
    let sub = 0, iva = 0;
    cart.forEach((it) => {
      const s = it.unit_price * it.quantity;
      sub += s;
      iva += s * (it.iva_rate || 0.15);
    });
    return { sub, iva, total: sub + iva };
  }, [cart]);

  const addToCart = (product) => {
    setCart((c) => {
      const idx = c.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const next = c.slice();
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...c, {
        product_id: product.id, name: product.name, sku: product.sku,
        unit_price: Number(product.sale_price), iva_rate: Number(product.iva_rate || 0.15),
        quantity: 1, stock: product.stock,
      }];
    });
    setSearch("");
  };

  const updateQty = (i, q) => {
    if (q <= 0) return setCart(cart.filter((_, idx) => idx !== i));
    const next = cart.slice();
    next[i] = { ...next[i], quantity: q };
    setCart(next);
  };

  const removeItem = (i) => setCart(cart.filter((_, idx) => idx !== i));

  const charge = async () => {
    if (cart.length === 0) return toast.error("Carrito vacío");
    if (!customer_id) return toast.error("Selecciona cliente");
    setSubmitting(true);
    try {
      const { data } = await api.post("/invoices", {
        customer_id, payment_method,
        items: cart.map((i) => ({
          product_id: i.product_id, quantity: i.quantity, unit_price: i.unit_price,
        })),
      });
      // Auto sign+send+authorize chain (best-effort; log failures but don't block POS flow)
      try { await api.post(`/invoices/${data.id}/sign`); } catch (err) { console.warn("POS auto-sign failed:", err); }
      try { await api.post(`/invoices/${data.id}/send`); } catch (err) { console.warn("POS auto-send failed:", err); }
      try { await api.post(`/invoices/${data.id}/authorize`); } catch (err) { console.warn("POS auto-authorize failed:", err); }
      const fresh = (await api.get(`/invoices/${data.id}`)).data;
      setLastInvoice(fresh);
      if (fresh.status === "autorizada") {
        toast.success(`Factura ${fresh.document_number} autorizada por SRI`);
      } else {
        toast.warning(`Factura ${fresh.document_number} creada en estado "${fresh.status}". Reintenta envío desde Facturas.`);
      }
      setCart([]);
      // refresh products to reflect new stock
      api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cobrar");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="pos-page" className="flex flex-col h-[calc(100vh-3rem)]">
      <PageHeader title="POS — Punto de venta" subtitle="Cobro rápido · F2 para buscar productos." testid="pos-header" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 flex-1 min-h-0">
        <div className="card-soft p-5 flex flex-col min-h-0">
          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9A92] pointer-events-none" />
            <input
              ref={searchRef}
              data-testid="pos-search"
              className="input-soft pl-9 text-base"
              placeholder="Buscar por SKU o nombre… (F2)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length > 0) { addToCart(filtered[0]); }
              }}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 overflow-y-auto pr-1" data-testid="pos-products-grid">
            {filtered.map((p) => (
              <button key={p.id} data-testid={`pos-product-${p.sku}`}
                onClick={() => addToCart(p)}
                disabled={Number(p.stock || 0) <= 0}
                className="text-left p-4 rounded-xl border border-[#E2E0D8] hover:border-[#2A3F35] hover:shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed">
                <p className="font-heading font-bold text-sm leading-tight line-clamp-2">{p.name}</p>
                <p className="text-[11px] text-[#8A9A92] font-mono mt-1">{p.sku}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-heading text-lg font-black">$ {Number(p.sale_price).toFixed(2)}</span>
                  <span className={`text-xs ${Number(p.stock || 0) > 0 ? "text-[#507A59]" : "text-[#A44A4A]"}`}>
                    {Number(p.stock || 0).toFixed(0)} en stock
                  </span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-[#8A9A92] py-12">
                <ScanLine className="w-12 h-12 mx-auto mb-2 opacity-40" />
                Sin resultados para &ldquo;{search}&rdquo;
              </div>
            )}
          </div>
        </div>

        <div className="card-soft p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag className="w-5 h-5 text-[#2A3F35]" />
            <h3 className="font-heading text-xl font-bold">Carrito ({cart.length})</h3>
          </div>

          <div className="space-y-2 overflow-y-auto flex-1 -mx-2 px-2" data-testid="pos-cart">
            {cart.length === 0 ? (
              <p className="text-sm text-[#8A9A92] text-center py-8">Agrega productos desde la búsqueda</p>
            ) : (
              cart.map((it, i) => (
                <div key={it.product_id} className="p-3 bg-[#FAFAF8] rounded-lg border border-[#E2E0D8]">
                  <div className="flex justify-between items-start mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{it.name}</p>
                      <p className="text-xs text-[#8A9A92] font-mono">{it.sku} · $ {it.unit_price.toFixed(2)}</p>
                    </div>
                    <button onClick={() => removeItem(i)} className="text-[#A44A4A] p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(i, it.quantity - 1)} className="w-7 h-7 rounded-md bg-[#F3F2ED] grid place-items-center hover:bg-[#E2E0D8]"><Minus className="w-3 h-3" /></button>
                      <input type="number" step="0.01" min="0" data-testid={`pos-qty-${it.sku}`}
                        className="w-16 text-center bg-white border border-[#E2E0D8] rounded-md py-1 text-sm font-bold"
                        value={it.quantity}
                        onChange={(e) => updateQty(i, Math.max(0, Number(e.target.value)))} />
                      <button onClick={() => updateQty(i, it.quantity + 1)} className="w-7 h-7 rounded-md bg-[#F3F2ED] grid place-items-center hover:bg-[#E2E0D8]"><Plus className="w-3 h-3" /></button>
                    </div>
                    <span className="font-bold">$ {(it.unit_price * it.quantity * (1 + (it.iva_rate || 0.15))).toFixed(2)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-[#E2E0D8] space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1 block">Cliente</span>
              <select data-testid="pos-customer-select" className="input-soft" value={customer_id} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Seleccione</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.identification})</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1 block">Forma de pago</span>
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_OPTIONS.map((p) => (
                  <button key={p.v} type="button" onClick={() => setPayment(p.v)}
                    className={`py-2 rounded-md text-sm font-semibold transition ${payment_method === p.v ? "bg-[#2A3F35] text-white" : "bg-[#F3F2ED] text-[#2A3F35]"}`}>
                    {p.l}
                  </button>
                ))}
              </div>
            </label>

            <div className="bg-[#FAFAF8] p-4 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#5C6B64]">Subtotal</span><span>$ {totals.sub.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6B64]">IVA 15%</span><span>$ {totals.iva.toFixed(2)}</span></div>
              <div className="flex justify-between font-heading text-2xl font-black pt-2 border-t border-[#E2E0D8]">
                <span>Total</span><span>$ {totals.total.toFixed(2)}</span>
              </div>
            </div>

            {selectedCustomer && (
              <SriStatusBanner
                customer={selectedCustomer}
                onRevalidated={(updated) => {
                  setCustomers((prev) => prev.map((c) => c.id === updated.id ? updated : c));
                }}
              />
            )}

            <button data-testid="pos-charge-button"
              onClick={charge} disabled={submitting || cart.length === 0 || !customer_id}
              className="btn-primary w-full text-base py-3 flex items-center justify-center gap-2">
              <Send className="w-4 h-4" /> {submitting ? "Procesando..." : "Cobrar y emitir factura"}
            </button>
          </div>
        </div>
      </div>

      {lastInvoice && (
        <LastInvoiceModal invoice={lastInvoice} customers={customers} onClose={() => setLastInvoice(null)} />
      )}
    </div>
  );
}

function LastInvoiceModal({ invoice, customers, onClose }) {
  const customer = customers.find((c) => c.id === invoice.customer_id);
  const waPhone = (customer?.phone || "").replace(/\D/g, "");
  const pdfUrl = `${API_BASE}/invoices/${invoice.id}/pdf`;
  const ticketUrl = `${API_BASE}/invoices/${invoice.id}/pdf?format=ticket`;
  const waText = encodeURIComponent(
    `Hola ${invoice.customer_name}, te enviamos tu factura electrónica ${invoice.document_number} por $ ${Number(invoice.total).toFixed(2)}. Descárgala aquí: ${pdfUrl}`,
  );
  const waLink = waPhone ? `https://wa.me/${waPhone.startsWith("593") ? waPhone : `593${waPhone.replace(/^0/, "")}`}?text=${waText}` : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="pos-success-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-7 border border-[#E2E0D8]">
        <div className="w-14 h-14 rounded-full bg-[#E4EFE6] grid place-items-center mx-auto mb-4">
          <Send className="w-7 h-7 text-[#507A59]" />
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92] text-center">Cobro exitoso</p>
        <h3 className="font-heading text-2xl font-black text-center mb-1">{invoice.document_number}</h3>
        <p className="text-center text-sm text-[#5C6B64] mb-5">{invoice.customer_name}</p>
        <div className="text-center font-heading text-4xl font-black mb-6">$ {Number(invoice.total).toFixed(2)}</div>

        <div className="grid grid-cols-2 gap-3">
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-secondary text-center" data-testid="pos-pdf-link">Factura A4</a>
          <a href={ticketUrl} target="_blank" rel="noreferrer" className="btn-secondary text-center" data-testid="pos-ticket-link">Ticket 76mm</a>
        </div>
        <div className="grid grid-cols-1 gap-3 mt-3">
          {waLink ? (
            <a href={waLink} target="_blank" rel="noreferrer" className="btn-primary text-center flex items-center justify-center gap-2" data-testid="pos-whatsapp-link">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zM6.597 20.13a9.86 9.86 0 005.451 1.591h.004c5.448 0 9.886-4.434 9.889-9.885a9.821 9.821 0 00-2.892-6.994 9.825 9.825 0 00-6.991-2.899c-5.452 0-9.887 4.434-9.889 9.885a9.86 9.86 0 001.516 5.26l.234.373-1.001 3.657 3.737-.988.34.2zm5.456-15.13c-.245-.005-.495-.005-.745-.005-.272 0-.715.103-1.089.512-.376.41-1.43 1.397-1.43 3.408 0 2.012 1.464 3.954 1.668 4.227.205.273 2.812 4.527 6.992 6.16.992.401 1.766.64 2.37.823.996.317 1.903.272 2.62.165.799-.12 2.461-1.006 2.808-1.978.347-.972.347-1.806.243-1.978-.103-.172-.376-.275-.787-.481-.412-.207-2.434-1.201-2.81-1.34-.376-.137-.65-.206-.926.207-.275.41-1.062 1.34-1.302 1.612-.24.275-.48.31-.892.103-.412-.206-1.737-.64-3.31-2.04-1.224-1.09-2.05-2.439-2.29-2.85-.24-.412-.026-.635.18-.84.185-.184.412-.48.618-.72.205-.24.273-.41.41-.685.137-.275.069-.515-.034-.722-.103-.207-.928-2.236-1.27-3.061z" /></svg>
              Enviar por WhatsApp
            </a>
          ) : (
            <button disabled className="btn-secondary opacity-50 cursor-not-allowed">Cliente sin teléfono</button>
          )}
        </div>
        <button onClick={onClose} className="w-full mt-3 text-sm text-[#8A9A92] hover:text-[#2A3F35] py-2" data-testid="pos-close-button">
          Continuar vendiendo
        </button>
      </div>
    </div>
  );
}
